import { useState, useEffect } from 'react';
import Modal from '@/v3-dashboard/components/UI/Modal';
import Input from '@/shared/components/UI/Input';
import Textarea from '@/shared/components/UI/Textarea';
import useFeedbackRecordingStore from '@/stores/feedbackRecording/feedbackRecordingStore';
import { submitFeedbackReport } from '@/services/feedbackRecording.service';
import useToast from '@/hooks/useToast';
import { useUserStore } from '@/stores/user/userStore';
import Checkbox from '@/shared/components/UI/Checkbox';
import RadioButton from '@/v3-dashboard/components/UI/RadioButton';
import PageFooterActions from '@/v3-dashboard/components/UI/PageFooterActions';

const FEEDBACK_TYPES = [
  { value: 'bug', label: 'Report Issue' },
  { value: 'feature', label: 'Share Idea' },
];

const CAPTURE_DELAY_MS = 3000;

const FeedbackRecordingModal = () => {
  const { isModalOpen, capturedEvents, closeModal, errorContext, clearErrorContext } =
    useFeedbackRecordingStore();
  const { addSuccessToast, addErrorToast } = useToast();
  const [form, setForm] = useState({ title: '', description: '', category: 'bug', wantsFollowUp: true });

  useEffect(() => {
    if (isModalOpen && errorContext) {
      setForm({
        category: 'bug',
        title: `Error: ${errorContext.statusCode} on ${errorContext.endpoint}`,
        description: `An error occurred during a ${errorContext.method} request.\n\nError message: ${errorContext.message}\nEndpoint: ${errorContext.endpoint}\nTimestamp: ${errorContext.timestamp}`,
        wantsFollowUp: true,
      });
    }
  }, [isModalOpen, errorContext]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    // Close modal and reset immediately - submission happens in the background
    const submittedForm = { ...form };
    clearErrorContext();
    closeModal();
    setForm({ title: '', description: '', category: 'bug', wantsFollowUp: true });
    addSuccessToast({ message: 'Thanks for your feedback! We appreciate you taking the time.' });

    // Delay capture so rrweb records the modal closing and surrounding UI state,
    // then submit in the background
    setTimeout(async () => {
      try {
        const getRecordingEvents = capturedEvents;
        const recording = typeof getRecordingEvents === 'function' ? getRecordingEvents() : getRecordingEvents;
        const user = useUserStore.getState().user;
        const metadata = {
          url: window.location.href,
          userAgent: navigator.userAgent,
          viewport: { width: window.innerWidth, height: window.innerHeight },
          timestamp: new Date().toISOString(),
          userId: user?.id,
          userEmail: user?.email,
          wantsFollowUp: submittedForm.wantsFollowUp,
        };

        await submitFeedbackReport({
          ...submittedForm,
          recording,
          metadata,
          wants_follow_up: submittedForm.wantsFollowUp,
          report_source: 'manual',
        });
      } catch (error) {
        addErrorToast({ message: 'Failed to submit feedback. Please try again.' });
      }
    }, CAPTURE_DELAY_MS);
  };

  const handleClose = () => {
    clearErrorContext();
    closeModal();
    setForm({ title: '', description: '', category: 'bug', wantsFollowUp: true });
  };

  const isReportIssue = form.category === 'bug';

  return (
    <Modal
      isOpen={isModalOpen}
      onClose={handleClose}
      title="Report an Issue or Share an Idea"
      titleClassName="font-lora"
      headerClassName="border-b-0 pb-v3-xs"
      className="md:!w-[600px] md:!rounded-[16px]"
    >
      <div>
        <div className="flex flex-col gap-v3-sm pb-v3-lg">
          {/* Feedback Type */}
          <div className="flex flex-col gap-v3-xs">
            <label className="text-v3-black-max text-v3-h10 font-medium">Feedback Type</label>
            <div className="flex flex-col gap-v3-xs">
              {FEEDBACK_TYPES.map((type) => (
                <RadioButton
                  key={type.value}
                  checked={form.category === type.value}
                  label={type.label}
                  value={type.value}
                  onChange={(val) => setForm((prev) => ({ ...prev, category: val }))}
                />
              ))}
            </div>
          </div>

          <Input
            label="Title/Summary"
            placeholder=""
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            helperText="Brief description"
          />

          <Textarea
            label="Details"
            placeholder=""
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            rows={6}
            textareaClassName="leading-6"
            helperText={isReportIssue ? 'What happened? What did you expect to happen?' : 'Tell us more about your idea'}
          />

          <div className="flex items-center gap-v3-xs">
            <Checkbox
              checked={form.wantsFollowUp}
              onChange={(val) => setForm((prev) => ({ ...prev, wantsFollowUp: val }))}
              label="I would like to hear back about this"
            />
          </div>
        </div>


        <PageFooterActions
          containerClassName="w-full flex items-center justify-between"
          actions={[
            {
              label: 'Cancel',
              variant: 'secondary',
              onClick: handleClose,
              size: 'lg',
            },
            {
              label: 'Send',
              variant: 'primary',
              onClick: handleSubmit,
              size: 'lg',
              disabled: !form.title.trim(),
            },
          ]}
        />
      </div>
    </Modal>
  );
};

export default FeedbackRecordingModal;
